import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * Option values (by option name, e.g. "Size") the customer picked themselves on this page view. The page
 * opens with the first size already selected, so the sticky button used to add that size without the
 * customer ever seeing the size picker (Teamwork 41867109). Kept outside the element, because a colour
 * change can render a new sticky bar; the size carried over to the new colour stays chosen.
 * @type {Map<string, string>}
 */
const chosenOptions = new Map();

/**
 * The ?variant= the page opened with. Only this one counts: a colour change later puts the new colour's
 * variant in the URL whether or not a size was picked.
 */
const pageVariantId = new URL(window.location.href).searchParams.get('variant');

/** Whether the options of the page's own ?variant= have been remembered. */
let pageVariantRead = false;

/** How long the sticky button waits for a colour change to finish before giving up. */
const COLOUR_CHANGE_TIMEOUT_MS = 10000;

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {string|null} Track last-processed source to avoid double-update */
  #lastProcessedSourceId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    // Also listen at document level: when the sticky bar is in a different
    // section than the product-information section, the section-scoped event
    // dispatched by variantPicker never reaches this element. The second
    // document-level VariantUpdateEvent dispatched by product-pdp-stitching.js
    // covers that gap. Signal-based cleanup handles teardown on disconnect.
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    // `click`, not `change`: tapping the size that is already selected (the first one, on page load)
    // confirms it too. A label tap and the arrow keys also click the radio.
    document.addEventListener('click', this.#handleOptionChange, { signal });
    document.addEventListener('click', this.#handleMainAddToCartClick, { capture: true, signal });

    this.#getInitialQuantity();
    this.#rememberPageVariantOptions();
    this.#syncOptionPrompt();
  }

  /**
   * A page opened with ?variant= (a size picked before a reload or Back, a cart item's link, a shared
   * link) already has that size chosen, so the sticky button doesn't ask for it again. Product cards
   * link without ?variant=, so a page opened from them still asks.
   */
  #rememberPageVariantOptions() {
    if (pageVariantRead || !pageVariantId || pageVariantId !== this.dataset.currentVariantId) return;

    const fieldsets = this.#getChoiceFieldsets();
    if (fieldsets.length === 0) return;
    pageVariantRead = true;

    for (const fieldset of fieldsets) {
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      if (selected instanceof HTMLInputElement) chosenOptions.set(this.#getOptionName(fieldset), selected.value);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up observer & scroll listener to watch buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block') || productForm.parentElement || productForm;
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    const checkVisibility = () => {
      const targetDevices = this.dataset.devices || 'both';
      const isMobile = window.innerWidth <= 749;

      if (targetDevices === 'desktop' && isMobile) {
        this.#hideStickyBar();
        return;
      }

      if (targetDevices === 'mobile' && !isMobile) {
        this.#hideStickyBar();
        return;
      }

      if (this.#hiddenByBottom) {
        this.#hideStickyBar();
        return;
      }

      const rect = buyButtonsBlock.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const headerComponent = document.querySelector('header-component');
      const headerBottom = headerComponent ? Math.max(0, headerComponent.getBoundingClientRect().bottom) : 0;

      // Check if main buy buttons are currently visible in the viewport (not hidden by header)
      const isMainAtcInViewport = rect.top < viewportHeight && rect.bottom > headerBottom;

      if (isMainAtcInViewport) {
        // Main buy buttons are visible in viewport - hide sticky bar
        this.#hideStickyBar();
      } else {
        // Main buy buttons are NOT in viewport (on load, at top of page, or scrolled past) - show sticky bar
        this.#showStickyBar();
      }
    };

    // IntersectionObserver triggers initial & layout checks
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(() => {
      checkVisibility();
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);

    // Scroll and resize listeners ensure instant, frame-perfect visibility updates on mobile
    window.addEventListener('scroll', checkVisibility, { passive: true, signal: this.#abortController.signal });
    window.addEventListener('resize', checkVisibility, { passive: true, signal: this.#abortController.signal });

    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else {
            this.#hiddenByBottom = false;
            checkVisibility();
          }
        },
        {
          rootMargin: '100px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]') || productForm.querySelector('button[type="submit"]');

    // Run initial check
    checkVisibility();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // A colour change swaps in the new colour's product a moment after the swatch is tapped; until then
    // the form still holds the previous colour, so wait instead of adding it.
    if (this.#isColourChanging()) {
      await this.#waitForColourChange();
      if (!this.isConnected) {
        // The colour change rendered a new sticky bar for the new colour: the tap is passed on to it.
        const newStickyAddToCart = /** @type {StickyAddToCartComponent | null} */ (
          document.querySelector('sticky-add-to-cart')
        );
        if (newStickyAddToCart && newStickyAddToCart !== this) newStickyAddToCart.handleAddToCartClick();
        return;
      }
      this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    }

    const unchosenOption = this.#getUnchosenOption();
    if (unchosenOption) {
      this.#promptForOption(unchosenOption);
      return;
    }

    const targetButton = this.#targetAddToCartButton;
    if (!targetButton || targetButton.disabled) return;

    const productForm = /** @type {HTMLElement & { addToCartPromise?: Promise<boolean> | null } | null} */ (
      targetButton.closest('product-form-component')
    );
    if (productForm?.addToCartPromise) return;

    targetButton.dataset.puppet = 'true';
    targetButton.click();

    const pendingAdd = productForm?.addToCartPromise;
    if (!pendingAdd) {
      targetButton.dataset.puppet = 'false';
      return;
    }

    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    const added = await pendingAdd;
    this.refs.addToCartButton.removeAttribute('aria-busy');
    if (!added) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    if (!cartIcon || !this.refs.productImage) {
      await onAnimationEnd(this.refs.addToCartButton);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    // Deduplication: when this component's own closest section is also an
    // ancestor of the element that dispatched the event, both the
    // section-scoped and document-level listeners above fire for the SAME
    // event as it bubbles. morph() reuses (moves) nodes out of
    // `event.detail.data.html` into the live DOM rather than cloning them,
    // so a second morph pass against the now-depleted source tree undoes
    // what the first pass just did. Same fix as product-price.js's
    // #lastProcessedSourceId / product-availability.js's updateAvailability.
    const incomingSourceId = event.detail?.sourceId;
    if (incomingSourceId && incomingSourceId === this.#lastProcessedSourceId) return;
    this.#lastProcessedSourceId = incomingSourceId ?? null;
    if (incomingSourceId) queueMicrotask(() => { if (this.#lastProcessedSourceId === incomingSourceId) this.#lastProcessedSourceId = null; });

    // For cross-product stitching swaps, event.detail.data.newProduct carries
    // the NEW product's id. Update our tracked productId first so we don't
    // bail out on the mismatch check below.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#syncOptionPrompt();
    // The main form re-renders its own button from the same response, possibly after this listener.
    requestAnimationFrame(() => this.#syncOptionPrompt());
  };

  /**
   * Remembers an option value the customer picks in the product page's variant picker.
   * @param {Event} event
   */
  #handleOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const fieldset = input.closest('fieldset.variant-option');
    if (!fieldset || !this.#getVariantPicker()?.contains(fieldset)) return;

    chosenOptions.set(this.#getOptionName(fieldset), input.value);
    fieldset.classList.remove('variant-option--needs-choice', 'variant-option--unchosen');
    fieldset.querySelector('.variant-option__choose-hint')?.remove();
    this.#syncOptionPrompt();
  };

  /**
   * The product page's variant picker (not the quick add drawer's or a product card's).
   * @returns {Element | null}
   */
  #getVariantPicker() {
    const section = this.#getProductForm()?.closest('.shopify-section');
    if (!section) return null;
    return (
      section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ??
      section.querySelector('variant-picker')
    );
  }

  /**
   * @param {Element} fieldset
   * @returns {string}
   */
  #getOptionName(fieldset) {
    // "Size: 3-4 years" - the label also shows the selected value after the colon.
    const label = fieldset.querySelector('.variant-option__legend-label')?.textContent ?? '';
    return label.split(':')[0]?.trim() ?? '';
  }

  /**
   * The options with a real choice to make: two or more values in stock, colour swatches aside. With only
   * one size in stock there is nothing to ask.
   * @returns {HTMLElement[]}
   */
  #getChoiceFieldsets() {
    const picker = this.#getVariantPicker();
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('fieldset.variant-option')).filter(
      (fieldset) =>
        fieldset instanceof HTMLElement &&
        !fieldset.matches('.variant-option--swatches, .variant-option--colour-discs') &&
        fieldset.querySelectorAll('input[type="radio"][data-option-available="true"]').length >= 2
    );
  }

  /**
   * The first option with a choice to make whose selected value the customer hasn't picked themselves -
   * it is still the one the page opened with, or a colour change moved it.
   * @returns {{ name: string, fieldset: HTMLElement } | null}
   */
  #getUnchosenOption() {
    this.#rememberPageVariantOptions();
    for (const fieldset of this.#getChoiceFieldsets()) {
      const name = this.#getOptionName(fieldset);
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      if (!(selected instanceof HTMLInputElement) || chosenOptions.get(name) !== selected.value) {
        return { name, fieldset };
      }
    }
    return null;
  }

  /**
   * While an option still needs a choice: its selected value doesn't look selected (the page picked it,
   * not the customer), and both the sticky and the main add to cart button read "Select Size" (or the
   * option's name).
   */
  #syncOptionPrompt() {
    const unchosenOption = this.#getUnchosenOption();
    const choiceFieldsets = this.#getChoiceFieldsets();

    for (const fieldset of this.#getVariantPicker()?.querySelectorAll('fieldset.variant-option') ?? []) {
      const unchosen =
        choiceFieldsets.includes(/** @type {HTMLElement} */ (fieldset)) &&
        chosenOptions.get(this.#getOptionName(fieldset)) !==
          /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input[type="radio"]:checked'))?.value;
      fieldset.classList.toggle('variant-option--unchosen', unchosen);
    }

    const { addToCartButton } = this.refs;
    const selectLabel = unchosenOption
      ? (this.dataset.selectOptionLabel ?? '').replace('[option]', unchosenOption.name)
      : null;
    this.#labelButton(addToCartButton, addToCartButton.querySelector('.add-to-cart-text__content > span'), selectLabel);
    if (selectLabel && !addToCartButton.disabled) {
      this.refs.quantityDisplay.style.display = 'none';
    } else {
      this.#updateButtonText();
    }

    const mainButton = this.#getMainAddToCartButton();
    if (mainButton) this.#labelButton(mainButton, mainButton.querySelector('.add-to-cart-text__content'), selectLabel);
  }

  /**
   * Puts "Select Size" on an add to cart button, or its own label back. Sold out / unavailable buttons
   * keep their text.
   * @param {HTMLButtonElement} button
   * @param {Element | null} text
   * @param {string | null} selectLabel
   */
  #labelButton(button, text, selectLabel) {
    if (!text) return;

    // A morph from the server puts the "Add to cart" / "Sold out" text back, without the marker.
    if (button.dataset.needsOption !== 'true') {
      button.dataset.addLabel = text.textContent?.trim() ?? '';
    }

    if (selectLabel && !button.disabled) {
      text.textContent = selectLabel;
      button.dataset.needsOption = 'true';
    } else if (button.dataset.needsOption === 'true') {
      text.textContent = button.dataset.addLabel ?? '';
      delete button.dataset.needsOption;
    }
  }

  /** @returns {HTMLButtonElement | null} The add to cart button of the product page's own form. */
  #getMainAddToCartButton() {
    const button = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * The main add to cart button asks for the size too, instead of adding the one the page selected.
   * @param {MouseEvent} event
   */
  #handleMainAddToCartClick = (event) => {
    const mainButton = this.#getMainAddToCartButton();
    if (!mainButton || !(event.target instanceof Node) || !mainButton.contains(event.target)) return;
    // The sticky button clicks it only once the size is chosen.
    if (mainButton.dataset.puppet === 'true') return;

    const unchosenOption = this.#getUnchosenOption();
    if (!unchosenOption) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#promptForOption(unchosenOption);
  };

  /**
   * Scrolls the option into view below the sticky header and asks for a choice.
   * @param {{ name: string, fieldset: HTMLElement }} option
   */
  #promptForOption({ name, fieldset }) {
    fieldset.classList.add('variant-option--needs-choice');
    if (!fieldset.querySelector('.variant-option__choose-hint')) {
      const hint = document.createElement('p');
      hint.className = 'variant-option__choose-hint';
      hint.setAttribute('role', 'alert');
      hint.textContent = (this.dataset.selectOptionHint ?? '').replace('[option]', name.toLowerCase());
      fieldset.querySelector('legend')?.after(hint);
    }

    const header = document.querySelector('header-component');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const top = fieldset.getBoundingClientRect().top + window.scrollY - headerBottom - 16;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // No focus on the selected size: its focus ring made it look picked. The hint is an alert, so screen
    // readers announce it.
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @returns {boolean} */
  #isColourChanging() {
    return Boolean(document.querySelector('product-pdp-stitching[aria-busy="true"]'));
  }

  /**
   * Resolves once the colour change has finished (assets/product-pdp-stitching.js clears aria-busy).
   * @returns {Promise<void>}
   */
  async #waitForColourChange() {
    const { addToCartButton } = this.refs;
    addToCartButton.setAttribute('aria-busy', 'true');
    const start = performance.now();
    while (this.#isColourChanging() && performance.now() - start < COLOUR_CHANGE_TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    addToCartButton.removeAttribute('aria-busy');
  }


  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');

    if (sectionElement) {
      const form = sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`)
        || sectionElement.querySelector('product-form-component');
      if (form) return form;
    }

    if (productId) {
      const form = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
      if (form) return form;
    }

    return document.querySelector('product-form-component');
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity ("Select Size" has none)
    if (available && this.#currentQuantity > 1 && addToCartButton.dataset.needsOption !== 'true') {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
